import type { Permission, PrincipalRole } from "@backchannel/tax-contracts";
import type { MessageKey } from "~/i18n/messages/pt-BR";
import type { DestinationIconName } from "./nav-icon";

export type { DestinationIconName };

type NavItemBase = {
  to: string;
  labelKey: MessageKey;
  icon: DestinationIconName;
};

/** A destination protected by one explicit Tax API permission. */
export type ProtectedNavItem = NavItemBase & {
  permission: Permission;
};

/** A destination available to any authenticated operator (for example, self-service settings). */
export type AuthenticatedNavItem = NavItemBase & {
  permission: null;
};

export type NavItem = ProtectedNavItem | AuthenticatedNavItem;
export type Destination = NavItem;

/**
 * Navigation is permission-honest: protected items an operator cannot use are
 * rendered, but marked as locked and not navigable, with the missing
 * permission named. Authenticated-only destinations such as Settings remain
 * usable even when the principal has no tax-domain read permission.
 *
 * Hiding them was the alternative and it is worse. An operator who cannot see
 * "Rulesets" concludes the platform has no rulesets, files a ticket asking for
 * a feature that already exists, and nobody learns that the real problem is a
 * role assignment.
 */
export const ACCOUNT_SETTINGS_PATH = "/settings";
export const ACCOUNT_USERS_PATH = "/users";
export const ACCOUNT_LEGAL_PATH = "/legal";
/**
 * Administração → Agentes (ADR 0012). A child of Administration for
 * super admins, not a rail destination, so it stays out of DESTINATIONS.
 */
export const AGENTS_PATH = "/admin/agents";
/** Administração → Pesquisa web: grounded web research sweeps (not the VM agent). */
export const WEB_RESEARCH_PATH = "/admin/web-research";
/** Administração → Demos: recorded agent runs saved to present the process. */
export const DEMOS_PATH = "/admin/demos";
/** Administração → Como funciona: the engine's process, animated over a real simulation. */
export const HOW_IT_WORKS_PATH = "/admin/how-it-works";
/** Administração → Registros do agente: findings audit per Brasília day. */
export const AGENT_FINDINGS_PATH = "/admin/agent-findings";
/** Administração → Planilhas com erro: failed calculator uploads, found by support code. */
export const UPLOAD_FAILURES_PATH = "/admin/upload-failures";
/** Administração → Sessões: session replay and the operator activity log. */
export const SESSIONS_PATH = "/admin/sessions";
/** Administração → Custo estimado da infraestrutura: the monthly GCP estimate. */
export const INFRA_COST_PATH = "/admin/infra-cost";
/** Administração → Autoaprovação: governed auto-approval pipeline (ADR 0020). */
export const AUTO_APPROVAL_PATH = "/admin/auto-approval";
export const RULE_ROUTE_GUIDE_PATH = "/rules/guide";
/** Regras → Verificação (ADR 0013): fiscal fact verification, platform admins only in the rail. */
export const RULES_VERIFICATION_PATH = "/rules/verification";
/** Lotes: the one lot history — every calculation job and what it returned. */
export const LOTS_PATH = "/lots";
/** Notas fiscais: the tax manager's NF-e workspace (upload, months, findings). */
export const INVOICES_PATH = "/invoices";
export const PRODUCTS_PATH = "/products";

/**
 * Platform administration (Agentes, Administração, Regras → Verificação) is a
 * super-admin concern. Role preview lowers `role`, so the preview hides it too.
 */
export function isPlatformAdmin(
  can: (permission: Permission) => boolean,
  role?: PrincipalRole | null,
): boolean {
  return role === "SUPER_ADMIN" && can("ADMIN:TAX_PLATFORM:ALL");
}

/** The two authenticated workspaces exposed from the account menu. */
export type Workspace = "operations" | "developers";

export const DEVELOPERS_WORKSPACE = "developers" as const;

/**
 * Developers is the tax-developer workspace: keys, docs, MCP, request
 * history and Audit. Super admins keep it. Tax managers do not, even though they can
 * hold MANAGE:TAX_INTEGRATION for API-level key work.
 */
export function canAccessDevelopers(
  can: (permission: Permission) => boolean,
  role?: PrincipalRole | null,
): boolean {
  return (role === "SUPER_ADMIN" && can("ADMIN:TAX_PLATFORM:ALL")) || role === "TAX_DEVELOPER";
}

export type DeveloperDestination = {
  id: "keys" | "requests" | "docs" | "mcp";
  hash: "integrations" | "requests" | "api-docs" | "mcp";
  labelKey: MessageKey;
  icon: DestinationIconName;
};

/**
 * Developers is a Settings workspace, so these links intentionally share the
 * existing route. The hash selects the section once the Settings surface has
 * mounted; no fourth top-level route is needed.
 */
export const DEVELOPER_DESTINATIONS: readonly DeveloperDestination[] = [
  {
    id: "keys",
    hash: "integrations",
    labelKey: "nav.developers.keys",
    icon: "key",
  },
  {
    id: "docs",
    hash: "api-docs",
    labelKey: "nav.developers.docs",
    icon: "book",
  },
  {
    id: "mcp",
    hash: "mcp",
    labelKey: "nav.developers.mcp",
    icon: "lab",
  },
  {
    id: "requests",
    hash: "requests",
    labelKey: "nav.developers.requests",
    icon: "logs",
  },
];

/** Settings without the workspace query remains the ordinary account page. */
export function isDevelopersWorkspace(pathname: string, search: string): boolean {
  if (pathMatchesDestination(pathname, "/audit")) return true;
  if (pathname !== "/settings") return false;
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  const values = new URLSearchParams(normalized).getAll("workspace");
  return values.length === 1 && values[0] === DEVELOPERS_WORKSPACE;
}

export function workspaceForLocation(pathname: string, search: string): Workspace {
  if (isDevelopersWorkspace(pathname, search)) return DEVELOPERS_WORKSPACE;
  return "operations";
}

/**
 * Route landmark focus follows the route and workspace. Query filters and
 * hashes change the view within a workspace and must not steal focus.
 */
export function workspaceFocusKey(pathname: string, workspace: Workspace): string {
  return `${pathname}\u0000${workspace}`;
}

export function developerHref(hash: DeveloperDestination["hash"]): string {
  return `/settings?workspace=${DEVELOPERS_WORKSPACE}#${hash}`;
}

export function developerDestinationForHash(hash: string): DeveloperDestination | undefined {
  const normalized = stripLocationHash(hash);
  return DEVELOPER_DESTINATIONS.find((item) => item.hash === normalized);
}

/** A lot's own page (/calculations/tasks/{id}) is reached from Lotes and belongs to it. */
const LOT_DETAIL_PATH = /^\/calculations\/tasks\/[^/]+\/?$/;

export function pathMatchesDestination(pathname: string, to: string): boolean {
  if (to === "/calculations/simulate") {
    if (LOT_DETAIL_PATH.test(pathname)) return false;
    return pathname === "/calculations" || pathname.startsWith("/calculations/");
  }
  if (to === LOTS_PATH && LOT_DETAIL_PATH.test(pathname)) return true;
  if (to === "/rules" && pathname === "/rules/library") return false;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function destinationForPath(pathname: string): Destination | undefined {
  return DESTINATIONS.find((item) => pathMatchesDestination(pathname, item.to));
}

/**
 * Destination catalog. Resultados and Importações still live inside Calculator
 * until they merge into Lotes; Audit lives in Developers. Users and Dados da
 * empresa belong to Administration; account settings stay in the account menu.
 */
export const DESTINATIONS: Destination[] = [
  {
    to: "/commercial",
    labelKey: "shell.nav.home",
    icon: "overview",
    permission: "READ:TAX_CALCULATION:ALL",
  },
  {
    to: "/overview",
    labelKey: "shell.nav.home",
    icon: "overview",
    permission: "READ:TAX_OVERVIEW:ALL",
  },
  {
    to: "/evidence",
    labelKey: "shell.nav.documents",
    icon: "evidence",
    permission: "READ:TAX_NEWS:ALL",
  },
  { to: "/sources", labelKey: "nav.sources", icon: "sources", permission: "READ:TAX_NEWS:ALL" },
  {
    to: "/proposals",
    labelKey: "nav.proposals",
    icon: "proposals",
    permission: "READ:TAX_RULE:ALL",
  },
  {
    to: "/rulesets",
    labelKey: "nav.publications",
    icon: "rulesets",
    permission: "READ:TAX_RULE:ALL",
  },
  { to: "/rules", labelKey: "shell.nav.rules", icon: "rules", permission: "READ:TAX_RULE:ALL" },
  {
    to: "/catalog/ncm",
    labelKey: "shell.nav.productSearch",
    icon: "rules",
    permission: "READ:TAX_RULE:ALL",
  },
  {
    to: "/rules/library",
    labelKey: "lookup.library",
    icon: "rules",
    permission: "READ:TAX_RULE:ALL",
  },
  {
    to: "/calculations/simulate",
    labelKey: "nav.calculator",
    icon: "calculator",
    permission: "READ:TAX_CALCULATION:ALL",
  },
  {
    to: LOTS_PATH,
    labelKey: "shell.nav.lots",
    icon: "lots",
    permission: "READ:TAX_CALCULATION:ALL",
  },
  {
    to: PRODUCTS_PATH,
    labelKey: "productCatalog.title",
    icon: "book",
    permission: "READ:TAX_CALCULATION:ALL",
  },
  {
    to: INVOICES_PATH,
    labelKey: "shell.nav.invoices",
    icon: "invoices",
    permission: "READ:TAX_NFE:ALL",
  },
  { to: "/audit", labelKey: "nav.audit", icon: "audit", permission: "READ:TAX_AUDIT:ALL" },
  {
    to: "/settings",
    labelKey: "nav.settings",
    icon: "settings",
    // Settings only reads the authenticated session and local appearance
    // state. It is intentionally not a tax-domain permission gate.
    permission: null,
  },
  {
    to: "/users",
    labelKey: "nav.users",
    icon: "users",
    permission: "ADMIN:TAX_PLATFORM:ALL",
  },
  {
    to: "/legal",
    labelKey: "nav.legal",
    icon: "legal",
    permission: null,
  },
];

export const OPERATIONAL_DESTINATIONS: Destination[] = DESTINATIONS.filter(
  (item) =>
    item.to !== "/commercial" &&
    item.to !== ACCOUNT_SETTINGS_PATH &&
    item.to !== ACCOUNT_LEGAL_PATH &&
    item.to !== ACCOUNT_USERS_PATH &&
    item.to !== "/audit",
);

/**
 * Available operational destinations. The shell shows Overview, Calculator,
 * Lot history and Rules as daily tools; reviews and publications fold into Rule governance.
 */
export const TAX_MANAGER_RAIL = [
  "/overview",
  "/calculations/simulate",
  LOTS_PATH,
  PRODUCTS_PATH,
  INVOICES_PATH,
  "/rules",
  "/catalog/ncm",
  "/sources",
  "/evidence",
  "/proposals",
  "/rulesets",
  "/rules/library",
] as const;

export const TAX_READER_RAIL = [
  "/overview",
  "/calculations/simulate",
  LOTS_PATH,
  PRODUCTS_PATH,
  INVOICES_PATH,
  "/rules",
  "/catalog/ncm",
  "/evidence",
  "/proposals",
  "/rulesets",
  "/sources",
  "/rules/library",
] as const;

export const TAX_COMMERCIAL_RAIL = [
  "/commercial",
  "/calculations/simulate",
  LOTS_PATH,
  INVOICES_PATH,
] as const;

export const TAX_DEVELOPER_RAIL = ["/overview", LOTS_PATH, PRODUCTS_PATH] as const;

export function operationalDestinationsForRole(role: PrincipalRole | null): Destination[] {
  const order =
    role === "TAX_COMMERCIAL"
      ? TAX_COMMERCIAL_RAIL
      : role === "TAX_MANAGER"
        ? TAX_MANAGER_RAIL
        : role === "TAX_READER"
          ? TAX_READER_RAIL
          : role === "TAX_DEVELOPER"
            ? TAX_DEVELOPER_RAIL
            : null;
  if (!order) return OPERATIONAL_DESTINATIONS;
  return order.flatMap((to) => DESTINATIONS.filter((item) => item.to === to));
}

/**
 * Sidebar model (UX plan §3). Daily tools are always visible; the rest are
 * collapsible groups. Paths are looked up in the role rail so role preview and
 * permission honesty keep working exactly as the flat rail did.
 */
export const DAILY_TOOL_PATHS = [
  "/commercial",
  "/overview",
  "/calculations/simulate",
  LOTS_PATH,
  PRODUCTS_PATH,
  INVOICES_PATH,
  "/rules",
] as const;

/** Governança: four direct pages, no Revisões/Publicações split. */
export const GOVERNANCE_PATHS = ["/proposals", "/evidence", "/sources", "/rulesets"] as const;

export type ShellLink = {
  id: string;
  to: string;
  labelKey: MessageKey;
  icon: DestinationIconName;
};

/** Agentes (super admin): automation, separate from managing people. */
export const AGENT_DESTINATIONS: readonly ShellLink[] = [
  { id: "agent-iob", to: AGENTS_PATH, labelKey: "shell.nav.agentIob", icon: "agents" },
  { id: "web-research", to: WEB_RESEARCH_PATH, labelKey: "nav.webResearch", icon: "webResearch" },
  {
    id: "agent-findings",
    to: AGENT_FINDINGS_PATH,
    labelKey: "shell.nav.agentLogs",
    icon: "agentFindings",
  },
  { id: "demos", to: DEMOS_PATH, labelKey: "nav.demos", icon: "demos" },
];

/** Administração (super admin): people, the legal entity and how the engine works. */
export const ADMINISTRATION_DESTINATIONS: readonly ShellLink[] = [
  { id: "crm", to: "/crm", labelKey: "crm.nav", icon: "users" },
  { id: "users", to: ACCOUNT_USERS_PATH, labelKey: "nav.users", icon: "users" },
  { id: "legal", to: ACCOUNT_LEGAL_PATH, labelKey: "nav.legal", icon: "legal" },
  {
    id: "upload-failures",
    to: UPLOAD_FAILURES_PATH,
    labelKey: "nav.uploadFailures",
    icon: "uploadFailures",
  },
  { id: "sessions", to: SESSIONS_PATH, labelKey: "nav.sessions", icon: "sessions" },
  { id: "infra-cost", to: INFRA_COST_PATH, labelKey: "nav.infraCost", icon: "infraCost" },
  {
    id: "auto-approval",
    to: AUTO_APPROVAL_PATH,
    labelKey: "nav.autoApproval",
    icon: "autoApproval",
  },
  { id: "how-it-works", to: HOW_IT_WORKS_PATH, labelKey: "nav.howItWorks", icon: "howItWorks" },
];

/** Regras → Verificação sits under Regras for platform admins. */
export const RULES_VERIFICATION_LINK: ShellLink = {
  id: "rules-verification",
  to: RULES_VERIFICATION_PATH,
  labelKey: "verification.tab",
  icon: "verification",
};

export function isRulesPath(pathname: string): boolean {
  return (
    pathname === "/rules" || pathname.startsWith("/rules/") || pathname.startsWith("/catalog/")
  );
}

export function shellLinkActive(pathname: string, to: string): boolean {
  if (to === DEMOS_PATH || to === SESSIONS_PATH) {
    return pathname === to || pathname.startsWith(`${to}/`);
  }
  return pathname === to;
}

export const LAST_OPERATION_STORAGE_KEY = "backchannel.tax.last-operation";

export type OperationLocation = {
  pathname: string;
  search: string;
  hash: string;
};

export type OperationNavigateTarget = {
  to: string;
  params?: Record<string, string>;
  search: Record<string, string>;
  hash?: string;
};

const DETAIL_PARAM_BY_SEGMENT = {
  evidence: "evidenceId",
  proposals: "proposalId",
  rulesets: "rulesetId",
  calculations: "calculationId",
  sources: "sourceId",
} as const;

const CALCULATION_COLLECTION_SEGMENTS = new Set(["tasks", "simulate"]);

type DetailSegment = keyof typeof DETAIL_PARAM_BY_SEGMENT;

function isDetailSegment(value: string): value is DetailSegment {
  return value in DETAIL_PARAM_BY_SEGMENT;
}

function isSafePathname(pathname: string): boolean {
  if (!pathname.startsWith("/") || pathname.startsWith("//")) return false;
  if (pathname.includes("://") || pathname.includes("\\") || /\s/.test(pathname)) {
    return false;
  }
  try {
    const url = new URL(pathname, "https://tax.backchannel.com.br");
    return url.origin === "https://tax.backchannel.com.br" && url.pathname === pathname;
  } catch {
    return false;
  }
}

export function stripLocationHash(hash: string): string {
  return hash.replace(/^#/, "");
}

export function searchRecordFromSearchStr(search: string): Record<string, string> {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  if (!normalized) return {};
  return Object.fromEntries(new URLSearchParams(normalized).entries());
}

export function operationHref(location: OperationLocation): string {
  const search = location.search
    ? location.search.startsWith("?")
      ? location.search
      : `?${location.search}`
    : "";
  const hashValue = stripLocationHash(location.hash);
  return `${location.pathname}${search}${hashValue ? `#${hashValue}` : ""}`;
}

export function parseStoredOperationLocation(
  value: string | null | undefined,
): OperationLocation | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<OperationLocation>;
    if (typeof parsed.pathname !== "string" || !isSafePathname(parsed.pathname)) {
      return null;
    }
    return {
      pathname: parsed.pathname,
      search: typeof parsed.search === "string" ? parsed.search : "",
      hash: typeof parsed.hash === "string" ? parsed.hash : "",
    };
  } catch {
    return null;
  }
}

export function canonicalizeOperationLocation(
  candidate: OperationLocation,
): OperationLocation | null {
  if (
    !isSafePathname(candidate.pathname) ||
    isDevelopersWorkspace(candidate.pathname, candidate.search)
  ) {
    return null;
  }
  const destination = destinationForPath(candidate.pathname);
  if (!destination || destination.to === ACCOUNT_SETTINGS_PATH) {
    return null;
  }

  if (
    candidate.pathname === RULE_ROUTE_GUIDE_PATH ||
    candidate.pathname === RULES_VERIFICATION_PATH
  ) {
    return {
      pathname: candidate.pathname,
      search: candidate.search,
      hash: candidate.hash,
    };
  }

  const runDetail = candidate.pathname.match(/^\/sources\/([^/]+)\/runs\/([^/]+)\/?$/);
  if (runDetail?.[1] && runDetail[2]) {
    return {
      pathname: `/sources/${runDetail[1]}/runs/${runDetail[2]}`,
      search: candidate.search,
      hash: candidate.hash,
    };
  }

  const collection = candidate.pathname.match(/^\/calculations\/([^/]+)\/?$/);
  if (collection?.[1] && CALCULATION_COLLECTION_SEGMENTS.has(collection[1])) {
    return {
      pathname: `/calculations/${collection[1]}`,
      search: candidate.search,
      hash: candidate.hash,
    };
  }

  const detail = candidate.pathname.match(
    /^\/(evidence|proposals|rulesets|calculations|sources)\/([^/]+)\/?$/,
  );
  if (detail?.[1] && detail[2] && isDetailSegment(detail[1])) {
    return {
      pathname: `/${detail[1]}/${detail[2]}`,
      search: candidate.search,
      hash: candidate.hash,
    };
  }

  if (candidate.pathname === destination.to || candidate.pathname === `${destination.to}/`) {
    return {
      pathname: destination.to,
      search: candidate.search,
      hash: candidate.hash,
    };
  }

  return {
    pathname: destination.to,
    search: candidate.search,
    hash: "",
  };
}

export function fallbackOperationLocation(
  can: (permission: Permission) => boolean,
  role: PrincipalRole | null = null,
): OperationLocation {
  const destination = operationalDestinationsForRole(role).find(
    (item) => item.permission === null || can(item.permission),
  );
  return { pathname: destination?.to ?? ACCOUNT_SETTINGS_PATH, search: "", hash: "" };
}

export function authorizedOperationLocation(
  candidate: OperationLocation | null,
  can: (permission: Permission) => boolean,
  role: PrincipalRole | null = null,
): OperationLocation {
  const canonical = candidate ? canonicalizeOperationLocation(candidate) : null;
  if (
    canonical &&
    !(role === "TAX_COMMERCIAL" && canonical.pathname === "/calculations/research")
  ) {
    const destination = destinationForPath(canonical.pathname);
    if (
      destination &&
      (role !== "TAX_COMMERCIAL" || TAX_COMMERCIAL_RAIL.some((to) => to === destination.to)) &&
      (destination.permission === null ||
        can(destination.permission) ||
        (destination.to === INVOICES_PATH && can("READ:TAX_RULE:ALL")))
    ) {
      return canonical;
    }
  }
  return fallbackOperationLocation(can, role);
}

export function operationNavigateTarget(location: OperationLocation): OperationNavigateTarget {
  const search = searchRecordFromSearchStr(location.search);
  const hash = stripLocationHash(location.hash) || undefined;
  if (location.pathname === "/evidence/workbook-review") {
    return { to: "/evidence/workbook-review", search, hash };
  }
  const runDetail = location.pathname.match(/^\/sources\/([^/]+)\/runs\/([^/]+)\/?$/);
  if (runDetail?.[1] && runDetail[2]) {
    return {
      to: "/sources/$sourceId/runs/$runId",
      params: { sourceId: runDetail[1], runId: runDetail[2] },
      search,
      hash,
    };
  }
  const collection = location.pathname.match(/^\/calculations\/([^/]+)\/?$/);
  if (collection?.[1] && CALCULATION_COLLECTION_SEGMENTS.has(collection[1])) {
    return {
      to: `/calculations/${collection[1]}`,
      search,
      hash,
    };
  }

  const detail = location.pathname.match(
    /^\/(evidence|proposals|rulesets|calculations|sources)\/([^/]+)\/?$/,
  );
  if (detail?.[1] && detail[2] && isDetailSegment(detail[1])) {
    const param = DETAIL_PARAM_BY_SEGMENT[detail[1]];
    return {
      to: `/${detail[1]}/$${param}`,
      params: { [param]: detail[2] },
      search,
      hash,
    };
  }
  return {
    to: location.pathname,
    search,
    hash,
  };
}

export function isReviewPath(pathname: string): boolean {
  return ["/proposals", "/evidence", "/rulesets", "/sources"].some((path) =>
    pathMatchesDestination(pathname, path),
  );
}
